import { memo } from 'react'
import { ActivityIndicator, Pressable, StyleSheet, View } from 'react-native'
import { Icon, Text, useTheme } from 'react-native-paper'
import { formatHoursReadable } from '@/lib/dates'

export interface JobSection {
    jobId: string
    jobName: string
    totalHours: number
    entryCount: number
}

interface JobSectionHeaderProps {
    section: JobSection
    expanded: boolean
    isLoading: boolean
    onToggle: () => void
}

export const JobSectionHeader = memo(function JobSectionHeader({
    section,
    expanded,
    isLoading,
    onToggle,
}: JobSectionHeaderProps) {
    const theme = useTheme()
    const entryCountText = `${section.entryCount} ${section.entryCount === 1 ? 'entry' : 'entries'}`

    return (
        <Pressable
            onPress={onToggle}
            style={[
                styles.container,
                {
                    borderBottomColor: theme.colors.outlineVariant,
                    backgroundColor: expanded
                        ? `${theme.colors.surfaceVariant}40`
                        : theme.colors.surface,
                },
            ]}
        >
            <View style={styles.content}>
                <View style={styles.titleRow}>
                    <Icon source="briefcase" size={16} color={theme.colors.onSurfaceVariant} />
                    <Text variant="titleMedium" style={{ fontWeight: '600' }} numberOfLines={1}>
                        {section.jobName}
                    </Text>
                </View>
                <View style={styles.secondaryRow}>
                    <Text
                        variant="bodySmall"
                        style={{ color: theme.colors.primary, fontWeight: '600' }}
                    >
                        {formatHoursReadable(section.totalHours)}
                    </Text>
                    <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                        ({entryCountText})
                    </Text>
                </View>
            </View>
            {isLoading ? (
                <ActivityIndicator size="small" color={theme.colors.primary} />
            ) : (
                <Icon
                    source={expanded ? 'chevron-up' : 'chevron-down'}
                    size={24}
                    color={theme.colors.onSurfaceVariant}
                />
            )}
        </Pressable>
    )
})

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 12,
        paddingHorizontal: 16,
        borderBottomWidth: 1,
        gap: 8,
    },
    content: {
        flex: 1,
        gap: 4,
    },
    titleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    secondaryRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
    },
})
